import { useEffect, useRef, useState } from 'react';
import { Box, Card, Flex, Text, Container } from 'theme-ui';
import PlayWhiteSmall from '~/assets/images/icons/play-white-small.svg';
import VideoPreviewAlt from '~/components/Generic/VideoPreview/alternative';
import ImgLoader from '~/components/Generic/ImgLoader';
import useMatchMedia from '~/hooks/utils/use-match-media';
import AnimatedButton from '~/components/Generic/AnimatedButton';
import ImageCarousel from '~/components/Generic/ImageCarousel';
import PageLink from '~/components/Generic/PageLink';
import toSnakeCase from '~/utils/to-snake-case';
import CollectionLink from '~/components/Collection/Link';

const SingleCard = ({
  contentCard,
  single = false,
  sideRef,
  margins,
  outerPaused,
  outerPausedFunc,
  cardClass,
  forwardSx = {},
}) => {
  const {
    label,
    title,
    titleAlt,
    mainVideo,
    cta,
    buttonName,
    descriptionMobile,
    description,
    // new types:
    fullWidth,
    mediaType,
    images,
    imagesMobile,
  } = contentCard;

  const [activeMedia, setActiveMedia] = useState(null);

  const email = cta?.innerLink?.toLowerCase().includes('mailto:');

  const ctaLink = cta?.page?.slug ?? cta?.url ?? cta?.innerLink;
  const ctaLinkLower = ctaLink?.toLowerCase() ?? null;

  let actionType = '';
  if (ctaLinkLower?.includes('womens')) actionType = 'hp_womens_product_spotlight';
  if (ctaLinkLower?.includes('mens') && !ctaLinkLower?.includes('womens')) actionType = 'hp_mens_product_spotlight';
  if (!ctaLinkLower?.includes('womens') && !ctaLinkLower?.includes('mens')) actionType = 'hp_activity_spotlight';
  if (ctaLinkLower?.includes('crew')) actionType = 'see_crew_story';
  if (label) actionType = label;

  const buttonLinkStyle = {
    fontSize: ['1.2rem', '1.3rem'],
    textTransform: 'uppercase',
    fontWeight: 'medium',
    textAlign: 'left',
    cursor: 'pointer',
  };

  const isMobile = useMatchMedia('(max-width: 768px)');

  const [paused, setPaused] = useState(true);
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const [hasMultiImages, setHasMultiImages] = useState(true);

  const playVideo = (e) => {
    e.preventDefault();
    setPaused(false);
    outerPausedFunc && outerPausedFunc(false);
    sideRef && (sideRef.current = true);
  };

  useEffect(() => {
    const currentImages = isMobile ? imagesMobile ?? images : images;
    setHasMultiImages(currentImages?.length > 1);
  }, [isMobile]);

  const buttonColors = {
    Black: { large: 'buttons.primary', small: 'buttons.primary.small' },
    White: { large: 'buttons.knockout', small: 'buttons.knockout.small' },
  };

  return (
    <Card
      className={cardClass ?? null}
      sx={{
        '--color': [fullWidth ? 'white' : 'black', 'white'],
        cursor: mainVideo ? (paused ? 'unset' : 'pointer') : ['unset', 'pointer'],
        position: 'relative',
        boxShadow: 'none',
        padding: 0,
        flexGrow: !paused && 0.0001,
        margin: paused && margins,
        transition: '1s ease',
        borderRadius: 0,

        '&::before': { content: "''" },
        h6: { position: 'relative' },
        '&:hover h6::before': { '@media (hover: hover)': { width: '100%' } },
        'h6::before': {
          content: '""',
          position: 'absolute',
          bottom: '-6px',
          background: !isMobile && 'white',
          width: '0',
          height: '2px',
          left: '50%',
          transform: 'translateX(-50%)',
          transition: 'width 0.3s ease-in-out',
        },
        '&:hover > div > .hover::before': { '@media (hover: hover)': { width: '100%' } },
        '& > div > .hover::before': {
          content: '""',
          position: 'absolute',
          bottom: '-6px',
          background: !isMobile && 'white',
          width: '0',
          height: '2px',
          left: '50%',
          transform: 'translateX(-50%)',
          transition: 'width 0.3s ease-in-out',
        },

        flexShrink: !paused && 0.0001,
        // ml: paused ? ['1rem', null, '2rem'] : 0,
        height: paused ? (single ? ['100%', '65rem'] : ['100%', '46rem']) : ['100%', '55rem'],
        // width: paused ? ['calc(100% - 2rem)', '100%'] : '100%',
        // maxWidth: paused,
        backgroundColor: !paused && 'black',
        img: { opacity: paused ? 1 : 0 },
        overflow: 'hidden',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        '.gatsby-plugin-image-wrapper > div': {
          pb: fullWidth ? '83.6% !important' : [0, '83.6% !important'],
        },
        ':hover > .media': {
          transform: mediaType === 'Carousel' && !hasMultiImages && [fullWidth && 'scale(1.05)', 'scale(1.05)'],
        },
        borderTopLeftRadius: !paused && '0 !important',
        borderBottomRightRadius: !paused && '0 !important',
        ...forwardSx,
      }}
    >
      {ctaLink ? (
        ctaLinkLower === 'mens' || ctaLinkLower === 'womens' ? (
          <CollectionLink
            sx={{
              display: [buttonName && 'none', paused ? 'block' : 'none'],
              position: 'absolute',
              height: '100%',
              width: '100%',
              top: mediaType === 'Carousel' && hasMultiImages ? '-62px' : 0,
              zIndex: 3,
            }}
            title={title}
            slug={ctaLinkLower}
          />
        ) : cta?.url || email ? (
          <a
            sx={{
              display: [buttonName && 'none', paused ? 'block' : 'none'],
              position: 'absolute',
              height: '100%',
              width: '100%',
              top: mediaType === 'Carousel' && hasMultiImages ? '-62px' : 0,
              zIndex: 3,
            }}
            onClick={(e) => {
              const event = new CustomEvent('custom-event', {
                bubbles: true,
                detail: {
                  category: 'ux_click',
                  action: actionType,
                  label:
                    mediaType === 'Carousel' && ctaLink?.includes('crew')
                      ? activeMedia?.title?.split('-')?.[2]?.trim()?.toLowerCase()
                      : title && toSnakeCase(title),
                },
              });
              e.target.dispatchEvent(event);
            }}
            href={cta?.url ?? ctaLink}
            target="_blank"
            rel="noopener noreferrer"
          />
        ) : (
          <PageLink
            forwardSx={{
              display: [buttonName && 'none', paused ? 'block' : 'none'],
              position: 'absolute',
              height: '100%',
              width: '100%',
              top: mediaType === 'Carousel' && hasMultiImages ? '-62px' : 0,
              zIndex: 3,
            }}
            slug={ctaLink ?? null}
          />
        )
      ) : (
        <Box
          onClick={mainVideo && !isMobile && ((e) => playVideo(e))}
          sx={{
            display: [buttonName && 'none', paused ? 'block' : 'none'],
            position: 'absolute',
            height: '100%',
            width: '100%',
            top: mediaType === 'Carousel' && hasMultiImages ? '-62px' : 0,
            zIndex: 3,
            cursor: 'pointer',
          }}
        />
      )}
      <MediaBlock
        setActiveMedia={setActiveMedia}
        single={single}
        buttonName={buttonName}
        paused={paused}
        fullWidth={fullWidth}
        mediaType={mediaType}
        images={isMobile ? imagesMobile ?? images : images}
        imagesMobile={imagesMobile}
      />

      {mainVideo && (
        <div sx={{ pointerEvents: paused ? 'none' : 'auto' }}>
          <VideoPreviewAlt
            playerName="Content Card Player"
            outerPaused={outerPaused}
            playerRef={playerRef}
            videoRef={videoRef}
            mainVideo={mainVideo}
            paused={paused}
            sideRef={sideRef}
            setPaused={setPaused}
            outerPausedFunc={outerPausedFunc}
          />
        </div>
      )}
      <Container
        sx={{
          opacity: paused ? 1 : 0,
          transition: '.2s ease',
          position: [fullWidth ? 'absolute' : 'relative', 'absolute'],
          bottom: buttonName ? [fullWidth && (mediaType === 'Carousel' && hasMultiImages ? '32px' : 0), '64px'] : '50%',
          transform: !buttonName && [fullWidth && 'translateY(50%)', 'translateY(50%)'],
          px: ['2rem', null, '6rem'],
          py: ['16px', mediaType === 'Carousel' && 0],

          pb: fullWidth ? ['22px', '0'] : 0,
          lineHeight: '90%',
          color: 'var(--color)',
        }}
      >
        {titleAlt && (
          <Text
            variant="h2"
            className={!buttonName && 'hover'}
            sx={{
              position: 'relative',
              width: ['100%', 'fit-content'],
              p: { margin: '0 !important', p: !buttonName && !fullWidth && 0 },
              fontSize: [
                buttonName ? (fullWidth ? '2.8rem !important' : '2rem') : fullWidth ? '4rem' : '2rem',
                '4rem !important',
                '5rem !important',
              ],
              br: { display: [buttonName ? 'none' : fullWidth ? 'unset' : 'none', 'unset'] },
              textAlign: [buttonName ? 'center' : fullWidth ? 'left' : 'center', 'left'],
              margin: 0,
              mb: [description && descriptionMobile ? '.8rem' : '1.6rem', '1.6rem', '2rem'],
              maxWidth: ['100%', '24.7rem', '40.5rem'],
            }}
            dangerouslySetInnerHTML={{ __html: titleAlt.childMarkdownRemark.html }}
          />
        )}

        {description && (
          <Box
            sx={{
              display: [descriptionMobile ? 'block' : 'none', 'block'],
              p: { m: [0, 'auto'] },
              maxWidth: ['100%', '326px'],
              mb: '1.6rem',
              textAlign: ['center', 'left'],
              lineHeight: ['1.68rem', '1.92rem'],
              fontSize: ['1.4rem', '1.6rem'],
              fontWeight: 400,
            }}
            dangerouslySetInnerHTML={{ __html: description.childMarkdownRemark.html }}
          />
        )}
        {isMobile && buttonName ? (
          <Flex sx={{ justifyContent: 'center' }}>
            {ctaLink ? (
              ctaLinkLower === 'mens' || ctaLinkLower === 'womens' ? (
                <CollectionLink sx={buttonLinkStyle} slug={ctaLink}>
                  <AnimatedButton
                    variant={buttonColors[fullWidth ? 'White' : 'Black']?.large}
                    sx={{
                      fontSize: '12px',
                      p: 0,
                      height: 'fit-content',
                      width: 'fit-content',
                      px: '24px',
                      py: '10px',
                      lineHeight: '29px',
                    }}
                  >
                    {buttonName ?? ''}
                  </AnimatedButton>
                </CollectionLink>
              ) : email || cta?.url ? (
                <a
                  href={cta?.url ?? ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    const event = new CustomEvent('custom-event', {
                      bubbles: true,
                      detail: {
                        category: 'ux_click',
                        action: actionType,
                        label:
                          mediaType === 'Carousel' && ctaLink?.includes('crew')
                            ? activeMedia?.title?.split('-')?.[2]?.trim()?.toLowerCase()
                            : title && toSnakeCase(title),
                      },
                    });
                    e.target.dispatchEvent(event);
                  }}
                >
                  {buttonName && (
                    <AnimatedButton
                      variant={buttonColors[fullWidth ? 'White' : 'Black']?.large}
                      sx={{
                        fontSize: '12px',
                        p: 0,
                        height: 'fit-content',
                        width: 'fit-content',
                        px: '24px',
                        py: '10px',
                        lineHeight: '29px',
                      }}
                    >
                      {buttonName}
                    </AnimatedButton>
                  )}
                </a>
              ) : (
                <PageLink slug={ctaLink} forwardSx={buttonLinkStyle}>
                  {buttonName && (
                    <AnimatedButton
                      variant={buttonColors[fullWidth ? 'White' : 'Black']?.large}
                      sx={{
                        fontSize: '12px',
                        p: 0,
                        height: 'fit-content',
                        width: 'fit-content',
                        px: '24px',
                        py: '10px',
                        lineHeight: '29px',
                      }}
                    >
                      {buttonName ?? ''}
                    </AnimatedButton>
                  )}
                </PageLink>
              )
            ) : (
              buttonName && (
                <AnimatedButton
                  onClick={mainVideo && ((e) => playVideo(e))}
                  variant={buttonColors[fullWidth ? 'White' : 'Black']?.large}
                  sx={{
                    fontSize: '12px',
                    p: 0,
                    height: 'fit-content',
                    width: 'fit-content',
                    px: '24px',
                    py: '10px',
                    lineHeight: '29px',
                  }}
                >
                  {buttonName}
                </AnimatedButton>
              )
            )}
          </Flex>
        ) : (
          <Box sx={{ width: 'fit-content', fontSize: '1.6rem' }}>
            {buttonName && (
              <Text as="h2" sx={buttonLinkStyle}>
                {!ctaLink && (
                  <PlayWhiteSmall onClick={mainVideo && ((e) => playVideo(e))} sx={{ fill: 'white', mr: '8px' }} />
                )}
                {buttonName}
              </Text>
            )}
          </Box>
        )}
      </Container>
    </Card>
  );
};

const MediaBlock = ({ mediaType, paused, images, isMobile, single, fullWidth, buttonName, ...props }) => {
  return (
    <Box
      className={'media'}
      sx={{
        height: fullWidth ? '100%' : [null, '100%'],
        mx: 'auto',
        opacity: paused ? 1 : 0,
        transition: '1s ease',
        backgroundColor: 'cloud',
      }}
    >
      {mediaType === 'Carousel' &&
        // Return carousel or single image
        // if === 1, static, else carousel

        (images?.length === 1 ? (
          <div
            sx={{
              filter: 'darker',
              // height: [fullWidth ? '100%' : 0, '100%'],
              width: '100%',
              height: '100%',

              //pb: [fullWidth ? 0 : '100%', 0],
              // overflow: 'hidden',
              pointerEvents: 'none',
              //'.gatsby-plugin-image-wrapper': { overflow: 'visible !important', div: { pb: '100% !important' } },
            }}
          >
            <ImgLoader
              image={images?.[0]}
              key={images.id}
              spinnerAbsolute
              spinnerSx={{ display: 'none' }}
              forwardSx={{
                display: ['block', 'none'],
                '@supports (aspect-ratio: 1/1)': { aspectRatio: '1/1' },
                '@supports not (aspect-ratio: 1/1)': {
                  paddingBottom: '100%',
                  height: 0,
                  width: '100%',
                },
              }}
            />
            <ImgLoader
              image={images?.[0]}
              key={images.id}
              spinnerAbsolute
              forwardSx={{ display: ['none', 'block'], height: [null, single ? '65rem' : '46rem'] }}
            />
          </div>
        ) : (
          images?.length > 1 && <CarouselMedia images={images} isMobile={isMobile} fullWidth={fullWidth} {...props} />
        ))}
    </Box>
  );
};

const CarouselMedia = ({ images, ctaLink, single, fullWidth, ...props }) => {
  return (
    <div
      sx={{
        height: fullWidth ? ['557px', '100%'] : ['0', '100%'],
        width: '100%',
        pb: fullWidth ? 0 : ['100%', 0],
        overflow: 'hidden',
        pointerEvents: 'auto',
        // '.gatsby-plugin-image-wrapper': { overflow: 'visible !important' },
      }}
    >
      <ImageCarousel
        forwardSx={{
          '.slick-slider .slick-dots': {
            display: 'flex !important',
            flexFlow: 'row nowrap',
            justifyContent: 'center',
            alignItems: 'center',
            bottom: ['1.7rem', '2.6rem'],

            position: 'absolute',
            zIndex: 10,
            pointerEvents: 'auto',
          },
          '.slick-slider': { backgroundColor: 'cloud', height: '100%' },

          img: {
            filter: 'darker',
          },

          '@media only screen and (max-width: 767px)': {
            '.gatsby-plugin-image-wrapper': {
              div: {
                pb: '100% !important',
              },
            },
          },
          overflow: 'visible',
          pb: [fullWidth ? 0 : '100%', 0],
          height: [fullWidth ? '100%' : 'auto', '100%'],
        }}
        imgStyleSx={{ height: '100%' }}
        dots={'white'}
        pauseOnHover={false}
        images={images}
        hoverPlay={false}
        disableFull={true}
        infiniteLoop={true}
        {...props}
      />
    </div>
  );
};

export default SingleCard;
